import { useState, useCallback, useRef, useEffect } from 'react';
import { useIkigaiData } from './useIkigaiData';
import { useIkigaiChat } from './useIkigaiChat';
import { XPNotification, DashboardUpdatePayload, Message, IkigaiBuckets, CareerPrototypeDoc } from '../types';
import { updatePrototypeModule, saveCareerPrototype, addMessage } from '../services/dbService';
import { generateCareerSuggestions } from '../services/geminiService';
import { useKai } from '../contexts/KaiPresenceContext';

interface UseIkigaiLogicProps {
    user: any;
    logout: () => Promise<void>;
    deleteAccount: () => Promise<void>;
}

export const useIkigaiLogic = ({ user, logout: _logout, deleteAccount }: UseIkigaiLogicProps) => {
    // Import useKai to trigger reactions
    const { triggerReaction } = useKai();

    // 1. Data Hook
    const {
        ikigaiData,
        setIkigaiData,
        userFacts,
        removeFact,
        userStats,
        careerPrototypes,
        setCareerPrototypes,
        currentQuest,
        setCurrentQuest,
        isQuestCompleted,
        setIsQuestCompleted,
        journeyHistory,
        isDataHydrating,
        saveStatus,
        saveJourneyItem,
        updatePrototypeState,
        resetData: resetDbData,
        mergeBuckets,
    } = useIkigaiData({ user });

    // 2. UI State
    const [xpNotification, setXpNotification] = useState<XPNotification | null>(null);
    const [errorToast, setErrorToast] = useState<string | null>(null);
    const [isSynthesizing, setIsSynthesizing] = useState(false);

    // 3. Chat Hook
    // We need a wrapper for onDashboardUpdate to handle XP and toasts
    const handleDashboardUpdate = useCallback((updates: DashboardUpdatePayload) => {
        // Merge buckets (Additions AND Removals)
        if (updates.passion || updates.mission || updates.profession || updates.vocation || updates.drains || updates.facts || updates.remove) {
            mergeBuckets({
                passion: updates.passion,
                mission: updates.mission,
                profession: updates.profession,
                vocation: updates.vocation,
                drains: updates.drains,
                facts: updates.facts,
                remove: updates.remove // Pass explicit removals
            });
        }

        // Handle Quest
        if (updates.quest) {
            setCurrentQuest(updates.quest);
            setIsQuestCompleted(false);
            setXpNotification({ amount: 100, reason: "New Quest Acquired" });
            triggerReaction('bounce'); // Celebration
        }

        // Handle XP for insights (if not just quest)
        // Check for ACTUAL content length to prevent empty array spam
        const hasInsights = 
            (updates.passion?.length || 0) > 0 || 
            (updates.mission?.length || 0) > 0 || 
            (updates.profession?.length || 0) > 0 || 
            (updates.vocation?.length || 0) > 0;

        if (hasInsights) {
             setXpNotification({ amount: 50, reason: "Ikigai Insight" });
             triggerReaction('bounce'); // Celebration
        }
    }, [mergeBuckets, setCurrentQuest, setIsQuestCompleted, triggerReaction]);

    const {
        chatMode,
        messages,
        timeCapsuleMessages,
        originStoryMessages,
        isLoading: chatLoading,
        setMessages,
        handleSendMessage,
        handleModeChange,
        clearChats,
        updateMessage
    } = useIkigaiChat({ 
        user, 
        ikigaiData, 
        userFacts,
        isDataHydrating, 
        onDashboardUpdate: handleDashboardUpdate 
    });

    // 4. Derived State
    const careerPaths = careerPrototypes.map(p => ({
        title: p.title,
        description: p.description,
        matchScore: p.matchScore,
        reasoning: p.reasoning,
        bucketMatches: p.bucketMatches // Pass through the new field
    }));

    // 5. Logic Wrappers
    const handleResetData = useCallback(async () => {
        await resetDbData();
        clearChats();
    }, [resetDbData, clearChats]);

    const handleDeleteAccount = useCallback(async () => {
        await resetDbData(); 
        await deleteAccount();
    }, [deleteAccount, resetDbData]);

    const handleUpdateSkillNode = useCallback(async (careerTitle: string, nodeId: string, status: 'locked' | 'unlocked' | 'mastered') => {
        // Find the prototype
        const prototype = careerPrototypes.find(p => p.title === careerTitle);
        if (!prototype || !prototype.skillTree) return;

        // Recursive update function for the tree
        const updateNode = (node: any): any => {
            if (node.id === nodeId) {
                return { ...node, status };
            }
            if (node.children) {
                return { ...node, children: node.children.map(updateNode) };
            }
            return node;
        };

        const newTreeData = updateNode(prototype.skillTree.data);
        
        // Update Hook State
        updatePrototypeState(careerTitle, { skillTree: { ...prototype.skillTree, data: newTreeData } });

        // Persist
        if (user?.uid) {
            try {
                await updatePrototypeModule(user.uid, careerTitle, 'skillTree', newTreeData);
            } catch (e) {
                console.error("Failed to save skill node update", e);
            }
        }

    }, [careerPrototypes, updatePrototypeState, user]);

    // 6. Blueprint Trigger Logic (Unified)
    const isFirstLoadRef = useRef<boolean>(true);
    const prevBucketsRef = useRef<IkigaiBuckets>(ikigaiData);

    useEffect(() => {
        // If we are hydrating, we reset the "First Load" flag.
        // This ensures that when hydration finishes, we treat the loaded data as the baseline (prev)
        // rather than diffing it against the initial empty state.
        if (isDataHydrating) {
            isFirstLoadRef.current = true;
            return;
        }

        // Initialize refs on first real load after hydration
        if (isFirstLoadRef.current) {
            prevBucketsRef.current = ikigaiData;
            isFirstLoadRef.current = false;
            return;
        }

        // Determine if new data was added
        const getDiff = (curr: string[], prev: string[]) => curr.filter(x => !prev.includes(x));
        
        const passionDiff = getDiff(ikigaiData.passion, prevBucketsRef.current.passion);
        const missionDiff = getDiff(ikigaiData.mission, prevBucketsRef.current.mission);
        const profDiff = getDiff(ikigaiData.profession, prevBucketsRef.current.profession);
        const vocDiff = getDiff(ikigaiData.vocation, prevBucketsRef.current.vocation);
        const drainsDiff = getDiff(ikigaiData.drains, prevBucketsRef.current.drains);

        const allNewItems = [...passionDiff, ...missionDiff, ...profDiff, ...vocDiff, ...drainsDiff];
        
        // Check if we have enough data to start synthesizing (at least 1 item total in any bucket)
        const hasData = ikigaiData.passion.length > 0 || ikigaiData.profession.length > 0 || ikigaiData.mission.length > 0 || ikigaiData.vocation.length > 0;

        // Unified Trigger: If we have ANY new items and we have at least some data, prompt the user.
        if (allNewItems.length > 0 && hasData) {
            // Check last message from current messages state to prevent spam
            // We also guard against cases where messages haven't loaded yet (messages.length === 0)
            const lastMsg = messages.length > 0 ? messages[messages.length - 1] : null;
            
            // Only trigger if:
            // 1. We actually have messages (chat history loaded)
            // 2. The last message wasn't already a generation prompt
            if (lastMsg && lastMsg.action !== 'generate_blueprints') {
                let text = "";
                
                // Case A: No Prototypes yet (Creation Phase)
                if (careerPrototypes.length === 0) {
                    text = "I've gathered enough context to create your first Life Prototypes. Ready to synthesize?";
                    if (passionDiff.length > 0) text = `With the addition of "${passionDiff[0]}", we have a strong core. Shall we generate your first prototypes?`;
                    else if (profDiff.length > 0) text = `Knowing you are good at "${profDiff[0]}" helps. Ready to generate prototypes?`;
                } 
                // Case B: Has Prototypes (Refinement Phase)
                else {
                    text = "I noticed you've added new insights. Would you like to regenerate your Future Prototypes?";
                    if (passionDiff.length > 0) text = `You've identified a new passion: "${passionDiff[0]}". This could completely change your trajectory. Should we regenerate your prototypes?`;
                    else if (profDiff.length > 0) text = `Adding "${profDiff[0]}" to your skills opens new doors. Want to update your career prototypes to leverage this?`;
                    else if (missionDiff.length > 0) text = `Aligning with the mission "${missionDiff[0]}" is powerful. Shall we adjust your prototypes to fit this?`;
                    else if (vocDiff.length > 0) text = `With "${vocDiff[0]}" identified as a market opportunity, we should re-calculate your financial viability. Ready?`;
                    else if (drainsDiff.length > 0) text = `Knowing that "${drainsDiff[0]}" drains you is crucial info. Let's filter your prototypes to explicitly avoid this.`;
                }

                const promptMsg: Message = {
                    id: `update-prompt-${Date.now()}`,
                    role: 'model',
                    text: text,
                    action: 'generate_blueprints',
                    createdAt: Date.now()
                };

                // Update Local State
                setMessages(prev => [...prev, promptMsg]);

                // Update DB (Fix: Ensure prompt exists for subsequent updates)
                if (user?.uid) {
                    addMessage(user.uid, 'standard', promptMsg).catch(e => 
                        console.error("Failed to save prompt message:", e)
                    );
                }
            }
        }

        // Update tracking ref
        prevBucketsRef.current = ikigaiData;

    }, [ikigaiData, isDataHydrating, careerPrototypes.length, setMessages, messages, user]);

    const handleSynthesize = useCallback(async () => {
        setIsSynthesizing(true);
        setXpNotification({ amount: 0, reason: "Synthesizing..." });
        
        try {
            const paths = await generateCareerSuggestions(ikigaiData);

            const newPrototypes: CareerPrototypeDoc[] = paths.map(p => ({
                id: p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
                title: p.title,
                description: p.description,
                matchScore: p.matchScore,
                reasoning: p.reasoning,
                bucketMatches: p.bucketMatches, // Store the mapping
                createdAt: Date.now()
            }));

            // Merge logic (LIFO Stack)
            setCareerPrototypes(prev => {
                const newIds = new Set(newPrototypes.map(p => p.id));
                const filteredOld = prev.filter(p => !newIds.has(p.id));
                return [...newPrototypes, ...filteredOld];
            });

            // Persist to DB
            if (user?.uid) {
                for (const p of newPrototypes) {
                    await saveCareerPrototype(user.uid, p);
                }
            }

            setXpNotification({ amount: 200, reason: "Blueprints Generated" });
            triggerReaction('bounce');

            // Log to Journey
            saveJourneyItem({
                type: 'milestone',
                title: 'Career Prototypes Generated',
                data: { count: paths.length },
                tags: ['career', 'synthesis']
            });

        } catch (e) {
            console.error("Synthesis error:", e);
            setErrorToast("Failed to generate career prototypes.");
        } finally {
            setIsSynthesizing(false);
        }
    }, [ikigaiData, setCareerPrototypes, setErrorToast, saveJourneyItem, user, triggerReaction]);

    return {
        chatMode,
        messages,
        timeCapsuleMessages,
        originStoryMessages,
        ikigaiData,
        userFacts,
        userStats,
        xpNotification,
        setXpNotification,
        careerPaths,
        careerPrototypes,
        currentQuest,
        isQuestCompleted,
        journeyHistory,
        isLoading: chatLoading || isSynthesizing,
        isDataHydrating,
        saveStatus,
        errorToast,
        setIkigaiData,
        setCareerPrototypes,
        setMessages,
        setIsQuestCompleted,
        setErrorToast,
        updatePrototypeState,
        removeFact,
        handleSendMessage,
        handleModeChange,
        handleResetData,
        handleDeleteAccount,
        handleUpdateSkillNode,
        mergeData: handleDashboardUpdate,
        saveJourneyItem,
        updateMessage,
        handleSynthesize 
    };
};